import * as Sentry from "@sentry/react";
import * as DateFns from "date-fns";
import React, { SetStateAction, useState } from "react";
export type { Argument as ClassValue } from "classnames";

export const Assert: {
  hard: (
    // tslint:disable-next-line
    condition: boolean | undefined | null,
    message: string,
    ...optionalParams: any[]
  ) => asserts condition;
  soft: (
    // tslint:disable-next-line
    condition: boolean | undefined | null,
    message: string,
    ...optionalParams: any[]
  ) => void;
} = {
  hard: (condition, message, ...optionalParams) => {
    if (condition !== true) {
      const assertionMessage = `Hard assertion failed: ${message}`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      console.warn(assertionMessage, ...optionalParams);
      throw new Error(assertionMessage);
    }
  },

  soft: (condition, message, ...optionalParams) => {
    if (condition !== true) {
      const assertionMessage = `Soft assertion failed: ${message}`;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      console.warn(assertionMessage, ...optionalParams);
      Sentry.captureMessage(assertionMessage);
    }
  },
};

export function no(message = "No message"): never {
  const assertionMessage = `no() called: ${message}`;
  console.warn(assertionMessage);
  throw new Error(assertionMessage);
}

// NOTE: isn't any different from a number, just for documentation purposes
export type Currency = number;

const EPSILON = 1e-12;
// NOTE: tailored for max value we can enter for Purchase Amount: 999,999,999.99
// so that 4,999,999.99 / 999,999,999.99 still rounds down

export function roundToNearestCent(amount: number) {
  return roundToDecimal(amount, 2);
}

export function roundToDecimal(
  input: number,
  decimals: number,
  epsilon: number = EPSILON,
) {
  return Number(toDecimalString(input, decimals, epsilon));
}

export function toDecimalString(
  input: number,
  decimals: number,
  epsilon: number = EPSILON,
) {
  return (input + epsilon).toFixed(decimals);
}

export function range(start: number, end: number, step = 1) {
  function* iterator() {
    for (let i = start; i < end; i += step) {
      yield i;
    }
  }
  return [...iterator()];
}

export function toObject<K extends string | number | symbol, V>(
  values: Array<[K, V]>,
) {
  return values.reduce(
    (acc, [key, value]) => ({ ...acc, [key]: value }),
    {} as { [k in K]: V },
  );
}

export function transformObjectAsKvps<
  T extends Record<string, unknown>,
  K extends string | number | symbol,
  V,
>(
  obj: T,
  f: (input: Array<[keyof T, T[keyof T]]>) => Array<[K, V]>,
): { [k in K]: V } {
  return toObject(f(Object.entries(obj) as Array<[keyof T, T[keyof T]]>));
}

export function isNotNull<T>(input: T | null): input is T {
  return input !== null;
}

export function sum(arr: number[]) {
  return arr.reduce((acc, num) => acc + num, 0);
}

export function sumFn<T>(arr: T[], getter: (el: T) => number): number {
  return sum(arr.map(getter));
}

export function formatISODate(d: Date) {
  return DateFns.format(d, "yyyy-MM-dd");
}

export function formatShortUIDate(d: Date) {
  return DateFns.format(d, "M/d/yyyy");
}

export function formatPhoneNumber(phone: string) {
  if (!/^[0-9]{10}$/.test(phone)) {
    return phone;
  } else {
    return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  }
}

type RangeType = Date | number;

export function createRange<T extends RangeType>(
  firstValue: T,
  lastValue: T,
): T[] {
  if (typeof firstValue === "number") {
    return createIntRange(firstValue, lastValue as number) as T[];
  } else {
    return DateFns.eachDayOfInterval({
      start: firstValue,
      end: lastValue,
    }) as T[];
  }
}

function createIntRange(firstValue: number, lastValue: number): number[] {
  if (
    firstValue !== Math.round(firstValue) &&
    lastValue !== Math.round(lastValue)
  ) {
    throw new Error("int expected in createIntRange");
  }

  const count = lastValue - firstValue + 1;

  if (count < 1) {
    return [];
  }

  return new Array(count).fill(0).map((_, index) => firstValue + index);
}

export function sortBy<T, U>(getter: (el: T) => U) {
  return (el1: T, el2: T) => {
    const val1 = getter(el1);
    const val2 = getter(el2);

    if (val1 < val2) {
      return -1;
    } else if (val1 > val2) {
      return 1;
    } else {
      return 0;
    }
  };
}

export function mapObject<T extends Record<string, unknown>, V>(
  object: T,
  f: (input: T[keyof T], k: keyof T) => V,
) {
  return Object.entries(object).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: f(value, key as keyof T),
    }),
    {} as { [k in keyof T]: V },
  );
}

export function areFloatsEqual(a: number, b: number, precisionDigits: number) {
  return compareFloat(a, b, precisionDigits) === 0;
}

export function compareFloat(a: number, b: number, precisionDigits: number) {
  const multiplier = Math.pow(10, precisionDigits);

  const getIntToCompare = (num: number) => Math.round(num * multiplier);

  const [intA, intB] = [a, b].map(getIntToCompare);

  return intA - intB;
}

export function xor(a: boolean, b: boolean) {
  return a !== b;
}

export function filterAndSplit<T>(array: T[], isValid: (el: T) => boolean) {
  return array.reduce<[T[], T[]]>(
    ([pass, fail], el) =>
      isValid(el) ? [[...pass, el], fail] : [pass, [...fail, el]],
    [[], []],
  );
}

export async function resolveInOrder<V, R>(
  inputs: V[],
  callback: (element: V, index: number, previousResults: R[]) => Promise<R>,
): Promise<R[]> {
  return inputs.reduce<Promise<R[]>>(
    async (accArrPromise, val, i) => {
      const prevResults = await accArrPromise;
      const currResult = await callback(val, i, prevResults);
      return [...prevResults, currResult];
    },

    Promise.resolve([]),
  );
}

const wholeUsdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatWholeDollars(dollars: Currency) {
  Assert.soft(
    Number.isInteger(dollars),
    `${dollars} is not a whole dollar amount`,
  );
  return wholeUsdFormatter.format(dollars);
}

const usdFormatterWithCents = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

export function formatDollarsAndCents(amount: Currency) {
  Assert.soft(
    Number.isInteger(100 * amount),
    `${amount} does not have a whole cent amount`, // NOTE: false positive for 2109.7
  );

  const absAmount = Math.abs(amount);
  const formatted = usdFormatterWithCents.format(absAmount);

  // parens for negative, use currencySign: "accounting" once es2020.intl is supported
  return amount >= 0 ? formatted : `(${formatted})`;
}

export function formatPercent(
  proportion: number,
  options?: { digits?: number },
) {
  return proportion.toLocaleString(undefined, {
    style: "percent",
    minimumFractionDigits: options?.digits ?? 2,
  });
}

export function ifNaN<A>(value: number, elseValue: (() => A) | A) {
  return !isNaN(value)
    ? value
    : elseValue instanceof Function
    ? elseValue()
    : elseValue;
}

export function isNodeEmpty(value: React.ReactNode) {
  return (
    value === undefined || value === null || value === true || value === false
  );
}

export function ifNodeEmpty(
  value: React.ReactNode,
  elseValue: (() => React.ReactNode) | React.ReactNode,
) {
  return !isNodeEmpty(value)
    ? value
    : elseValue instanceof Function
    ? elseValue()
    : elseValue;
}

export function hasValue<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export const hv = hasValue;

export function hasValueFn<T, A>(
  value: T,
  thenFn: (value: Exclude<T, null | undefined>) => A,
): A | Extract<T, null | undefined> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  return hasValue(value) ? thenFn(value as any) : value;
}

export function sumDefined(items: Array<number | undefined | null>) {
  return sum(items.map(e => e ?? 0));
}

export function getFullName(user: { firstName: string; lastName: string }) {
  return `${user.firstName} ${user.lastName}`;
}

export type StateSetter<T> = React.Dispatch<SetStateAction<T>>;

export function capitalizeFirstLetter<K extends string>(str: K) {
  return (
    str.length > 0 ? `${str[0].toUpperCase()}${str.slice(1)}` : ""
  ) as Capitalize<K>;
}

export function uncapitalizeFirstLetter<K extends string>(str: K) {
  return (
    str.length > 0 ? `${str[0].toLowerCase()}${str.slice(1)}` : ""
  ) as Uncapitalize<K>;
}

type CalendarDayCompareOperation = "<" | "<=" | "==" | ">=" | ">";

export function compareCal(
  date1: Date,
  operation: CalendarDayCompareOperation,
  date2: Date,
) {
  const diff = DateFns.differenceInCalendarDays(date1, date2);

  switch (operation) {
    case "<":
      return diff < 0;
    case "<=":
      return diff <= 0;
    case "==":
      return diff === 0;
    case ">=":
      return diff >= 0;
    case ">":
      return diff > 0;
    default:
      throw new Error(`Couldn't find operation: ${operation}`);
  }
}

export function tee<A>(a: A) {
  console.log(a);
  return a;
}

export function groupBy<A, B>(arr: A[], getKey: (input: A) => B) {
  return arr.reduce((acc, item) => {
    const key = getKey(item);
    if (!acc.has(key)) {
      acc.set(key, []);
    }
    acc.get(key)!.push(item);
    return acc;
  }, new Map<B, A[]>());
}

export function formatYesNo(val: boolean): string {
  return val ? "Yes" : "No";
}

export function useCounterKey(start = 0): [number, () => void] {
  const [count, setCount] = useState(start);
  return [count, () => setCount(k => k + 1)];
}

export function iterToArray<A>(iterator: () => IterableIterator<A>) {
  return [...iterator()];
}
